import clsx from 'clsx'
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  parseISO
} from 'date-fns'
import { TFunction } from 'i18next'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Avatar from '@/components-latest/primitives/Avatar'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import MoreOptions, {
  MoreOptionsItem
} from '@/components-latest/primitives/MoreOptions'
import Text from '@/components-latest/primitives/Text'
import DisableConfirmation from '@/components-latest/ui/OfferPage/DisableConfirmation'
import { SupportedLanguages } from '@/config/i18n'
import { IMenuItem } from '@/ducks/menu/menu.types'
import { useDisableOfferMutation } from '@/ducks/offers/api/offers.api'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'
import { useServerTranslation } from '@/hooks/useServerTranslation/useServerTranslation'
import { formatPrice } from '@/utils/currency.utils'

import Styles from './OfferItem.module.scss'
import { PATHS } from '@/config/router/routes'
import { useHistory } from 'react-router-dom'

const getLeftTimeTranslation = (time: string, t: TFunction): string => {
  if (!time) return ''

  try {
    const target = parseISO(time)
    const now = new Date()

    const minutesDiff = differenceInMinutes(target, now)
    const hoursDiff = differenceInHours(target, now)
    const daysDiff = differenceInDays(target, now)

    if (daysDiff > 0) {
      return t('offers.days-left', { count: daysDiff })
    } else if (hoursDiff > 0) {
      return t('offers.hours-left', { count: hoursDiff })
    } else if (minutesDiff > 0) {
      return t('offers.minutes-left', { count: minutesDiff })
    } else {
      return t('offers.minutes-left', { count: 0 })
    }
  } catch (error) {
    console.error('Error parsing date:', error)
    return ''
  }
}

type Props = {
  className?: string
  menuItem: IMenuItem
  onEdit?: (itemId: string) => void
  onDisable?: (itemId: string) => void
}

const OfferItem = React.memo(({ className, menuItem, onEdit }: Props) => {
  const { t, i18n } = useTranslation()
  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages
  const { getTranslation } = useServerTranslation()

  const [disableOffer] = useDisableOfferMutation()

  const [disableModalIsOpen, setDisableModalIsOpen] = useState(false)

  const currency = useSelector(selectCurrencyCode) || ''
  const history = useHistory()
  const { name, _id, avatar, price } = menuItem
  const handleEdit = () => {
    history.push(PATHS.OFFERS_CREATE, {
      state: {
        isEditing: true,
        offerId: menuItem._id,
        initialDiscount: menuItem.offer?.amount,
        initialExpiration: menuItem.offer?.offer_validity,
        name: getTranslation(menuItem.name),
      }
    })
  }
  return (
    <ContentBlock
      data-testid={`offer-item-${_id}`}
      className={clsx(className, Styles.wrapper)}>
      <div className={Styles.content}>
        <Avatar image={avatar} squared size="medium" string={name[language]} />
        <Text
          className={Styles.name}
          asChild
          variant="nextSubtitle"
          weight="extraBold">
          <p>{name[language]}</p>
        </Text>
      </div>
      <div className={Styles.detailsBlock}>
        <Text
          className={Styles.price}
          asChild
          variant="nextSubtitle"
          weight="regular">
          <p>
            {formatPrice(price || 0, currency, {
              signDisplay: 'never'
            })}
          </p>
        </Text>
        <div className={Styles.badges}>
          <div className={clsx(Styles.tag, Styles.negative)}>
            <Text variant="nextCaption">-{menuItem.offer?.amount || 0}%</Text>
          </div>
          <div className={clsx(Styles.tag, Styles.info)}>
            <Text variant="nextCaption">
              {menuItem.offer?.offer_valid_until &&
                getLeftTimeTranslation(menuItem.offer?.offer_valid_until, t)}
            </Text>
          </div>
        </div>
      </div>

      <MoreOptions
        popoverClassName={Styles.optionsContainer}
        data-testid={`edit-menu-item-more-${menuItem._id}`}
        align="start"
        noArrow>
        <MoreOptionsItem
          className={Styles.option}
          id={menuItem._id}
          onClick={handleEdit}>
          <Text variant="nextSubtitle">{t('offers.edit-offer')}</Text>
        </MoreOptionsItem>
        <MoreOptionsItem
          className={Styles.option}
          id={menuItem._id}
          onClick={() => setDisableModalIsOpen(true)}>
          <Text variant="nextSubtitle">{t('offers.disable-offer')}</Text>
        </MoreOptionsItem>
      </MoreOptions>

      <DisableConfirmation
        open={disableModalIsOpen}
        onOpenChange={setDisableModalIsOpen}
        onConfirm={() => disableOffer([menuItem._id])}
        title={t('offers.disable-modal.title')}
        description={t('offers.disable-modal.description', {
          name: getTranslation(menuItem.name)
        })}
        cancelText={t('common.modal.cancel')}
        confirmText={t('common.modal.confirm')}
      />
    </ContentBlock>
  )
})

OfferItem.displayName = 'OfferItem'
export default OfferItem
